import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTimer } from "react-timer-hook";
import OtpInput from "react-otp-input";
import ReCAPTCHA from "react-google-recaptcha-enterprise";

import { SITE_KEY } from "constants/config";
import { useGenerateOtp } from "store/backend/apiHooks";
import { useLoginWithOtp } from "store/backend/useLogin";
import useAuth from "../../hooks/useAuth";
import { PrimaryButton, Textfield } from "widgets";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import styles from "./Login.module.scss";

const isValidPhone = (phone) => /^[0-9]{10}$/.test(phone);

const handleGenerateOtpClick = async ({
  phone,
  recaptchaRef,
  setStatus,
  generateOtp,
  openSnackbar,
  setStep,
  restart,
}) => {
  if (!isValidPhone(phone)) {
    setStatus("Please enter a valid 10-digit phone number");
    return;
  }

  try {
    setStatus("");

    const captchaToken = await recaptchaRef.current.executeAsync("generate_otp");
    recaptchaRef.current.reset();

    generateOtp(
      { phone, captcha_token: captchaToken },
      {
        onSuccess: () => {
          openSnackbar("OTP sent successfully!", "success");
          setStep("OTP");

          const newExpiry = new Date();
          newExpiry.setSeconds(newExpiry.getSeconds() + 60);
          restart(newExpiry);
        },
        onError: (error) => {
          openSnackbar(error?.message || "Error sending OTP", "error");
        },
      }
    );
  } catch (err) {
    setStatus(err?.message || "Failed to get captcha token");
  }
};

const handleLoginClick = ({
  otp,
  phone,
  setStatus,
  loginWithOtp,
  authLogin,
  navigate,
  openSnackbar,
}) => {
  if (otp.trim().length < 4) {
    setStatus("Please enter a valid OTP");
    return;
  }

  loginWithOtp(
    { phone, otp },
    {
      onSuccess: (data) => {
        authLogin(data.access_token);
        localStorage.setItem("cashierCRNId", data.crn_id);
        localStorage.setItem("roles", data.roles);
        localStorage.setItem("cashierPhoneNumber", phone);

        openSnackbar("Login successful!", "success");
        navigate("/homePage");
      },
      onError: (error) => setStatus(error.message),
    }
  );
};


const OtpLogin = () => {
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();

  const { openSnackbar } = useSnackbarContext();
  const { login: authLogin } = useAuth();

  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("PHONE");
  const [status, setStatus] = useState("");

  const { mutate: generateOtp, isPending: isOtpLoading } = useGenerateOtp();
  const { mutate: loginWithOtp, isPending: isLoggingIn } = useLoginWithOtp();

  const initialExpiry = new Date();
  initialExpiry.setSeconds(initialExpiry.getSeconds());
  
  const { seconds, restart } = useTimer({
    expiryTimestamp: initialExpiry,
    autoStart: false,
  });

  return (
    <div className={styles.mainContainer}>
      <ReCAPTCHA sitekey={SITE_KEY} size="invisible" ref={recaptchaRef} />

      {step === "PHONE" ? (
        <>
          <div className={styles.heading}>Login with phone number</div>

          <div className={styles.fieldWrapper}>
            <Textfield
              label="Phone Number"
              fullWidth
              margin="normal"
              value={phone}
              handleOnChange={(e) =>
                setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))
              }
            />
          </div>

          <div className={styles.fieldWrapper}>
            <PrimaryButton
              onClick={() =>
                handleGenerateOtpClick({
                  phone,
                  recaptchaRef,
                  setStatus,
                  generateOtp,
                  openSnackbar,
                  setStep,
                  restart,
                })
              }
              disabled={!isValidPhone(phone)}
              fullWidth
            >
              {isOtpLoading ? "Loading..." : "Request OTP"}
            </PrimaryButton>
          </div>

          {status && <div className={styles.errorText}>{status}</div>}
        </>
      ) : (
        <>
          <ArrowBackIosIcon
            className={styles.goBackIcon}
            onClick={() => {
              setStep("PHONE");
              setOtp("");
              setStatus("");
            }}
          />

          <div className={styles.heading}>OTP Verification</div>

          <div className={styles.subHeading}>
            We have sent an OTP to <b>{phone}</b>
          </div>

          <div className={styles.fieldWrapper}>
            <OtpInput
              value={otp}
              onChange={setOtp}
              numInputs={4}
              isInputNum
              renderSeparator={<span>-</span>}
              renderInput={(props) => (
                <input {...props} className={styles.otpInput} />
              )}
              containerClassName={styles.otpContainer}
            />
          </div>

          <div className={styles.resendWrapper}>
            {seconds > 0 ? (
              <div className={styles.resendOtpText}>
                Resend OTP in {seconds}s
              </div>
            ) : (
              <div
                onClick={() =>
                  handleGenerateOtpClick({
                    phone,
                    recaptchaRef,
                    setStatus,
                    generateOtp,
                    openSnackbar,
                    setStep,
                    restart,
                  })
                }
                className={styles.resendOtpLink}
              >
                Resend OTP
              </div>
            )}
          </div>

          <div className={styles.fieldWrapper}>
            <PrimaryButton
              onClick={() =>
                handleLoginClick({
                  otp,
                  phone,
                  setStatus,
                  loginWithOtp,
                  authLogin,
                  navigate,
                  openSnackbar,
                })
              }
              disabled={isLoggingIn || otp.trim().length < 4}
              fullWidth
            >
              {isLoggingIn ? "Loading..." : "Verify & Login"}
            </PrimaryButton>
          </div>

          {status && <div className={styles.errorText}>{status}</div>}
        </>
      )}
    </div>
  );
};

export default OtpLogin;
